
import { CalendarState } from "@/utils/calendar-state";
import AppFullCalendar from "@fullcalendar/vue";
import Vue from "vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import { EventSourceInput } from "@fullcalendar/core/structs/event-source";
import { Calendar } from "@/models/calendar";
import { CalendarService, GoogleCalendarService, TaskService } from "@/services";
import { Folder } from "@/models/tasks/folder";
import * as FullCalendarApi from "@fullcalendar/core";
import { CalendarEvent } from "@/models/calendar-event";
import { v4 } from "uuid";


export default Vue.extend({
	data(){
		return{
			preventSwipe: false,
			scrollSubs: [] as {el:HTMLElement}[],
			isRefreshingCals: false,
			versionKey: v4()
		};
	},
	props: {
		state: {
			type: Object as ()=>CalendarState,
			required: true
		},
		isDragging: Boolean
	},
	computed: {
		calendars(): Calendar[] {
			return this.state.selectedCalendars;
		},
		timeFormat(): any {
			return {
				hour: "numeric",
				minute: "numeric",
				meridiem: false,
				hour12: false
			};
		},
		googleCalendars(): gapi.client.calendar.CalendarListEntry[] {
			return this.state.selectedGoogleCalendars;
		},
		calendarList():{id:number, name:string, source:EventSourceInput, views:any}[]{
			return this.calendars.map(c=>{
				return {
					id: c.ID,
					name: c.Name,
					source: CalendarService.createEventSource([c]),
					views: {
						timeGridDay: {
							type: "timeGridDay",
							columnHeaderText(date: Date) {
								return c.Name;
							},
							allDayText: ""
						},
					}
				};
			});
		},
		calendarEventSources(): EventSourceInput[] {
			let items = [
				...(this.googleCalendars.map(c => {
					return GoogleCalendarService.createFullCalendarEventSource(c);
				})),
				CalendarService.createEventSource(this.calendars),
				TaskService.createFullCalendarEventSource(this.state.selectedTaskFolders.map(f=>f.Folder as Folder))
			] as EventSourceInput[];
			return items;
		},
		calendarPlugins(): any[] {
			return [ // plugins must be defined in the JS
				dayGridPlugin,
				timeGridPlugin,
				interactionPlugin // needed for dateClick
			];
		},
		width():string{
			return `${100/this.calendars.length}%`;
		},
		minTime():string {
			let minTime = "";
			for (let cal of this.calendars) {
				if (!minTime) {
					minTime = cal.MinTime;
					continue;
				}
				if (!cal.MinTime) {
					return "";
				}
				if (Calendar.isTimeSmaller(cal.MinTime, minTime)){
					minTime = cal.MinTime;
				}
			}
			return minTime;
		},
		maxTime():string {
			let maxTime = "";
			for (let cal of this.calendars) {
				if (cal.MaxTime == "00:00:00"){
					return "";
				}
				if (!maxTime) {
					maxTime = cal.MaxTime;
					continue;
				}
				if (!cal.MaxTime) {
					return "";
				}
				if (Calendar.isTimeSmaller(maxTime, cal.MaxTime)){
					maxTime = cal.MaxTime;
				}
			}
			if (maxTime == "00:00:00"){
				return "";
			}
			return maxTime;
		}
	},
	watch: {
		"calendars": {
			immediate: true,
			async handler(){
				this.refreshCals();
			}
		},
		minTime(){
			this.refreshCals();
		},
		maxTime(){
			this.refreshCals();
		}
	},
	methods: {
		refreshCals(){
			if (this.isRefreshingCals) return;
			this.versionKey = v4();
			this.isRefreshingCals = true;
			setTimeout(()=>{
				this.isRefreshingCals = false;
				let els = this.$refs.fullCalendar as any[];
				let apis:FullCalendarApi.Calendar[] = [];
				for (let el of els){
					apis.push(el.getApi());
				}
				this.state.setCalendarApis(apis);
				this.linkScrolls();
			}, 100);
		},
		onScroll(event:Event){
			for (let el of this.scrollSubs){
				if (el.el == event.target) continue;
				el.el.scrollTop = (event.target as HTMLElement).scrollTop;
			}
		},
		scrollAlTo(scrollTop:number){
			for (let el of this.scrollSubs){
				console.log("Scrolling", scrollTop);
				el.el.scrollTop = scrollTop;
			}
		},
		linkScrolls(){
			for (let sub of this.scrollSubs) {
				sub.el.removeEventListener("scroll", this.onScroll);
			}
			this.scrollSubs = [];
			let els = (this.$refs.fullCalendar as Vue[]).map(e=>e.$el as HTMLElement);
			for (let el of els){
				let scroller = el.getElementsByClassName("fc-scroller")[0];
				if (!scroller) continue;
				scroller.addEventListener("scroll", this.onScroll);
				this.scrollSubs.push({el: scroller as HTMLElement});
			}
		},
		handleDateClick(event: { date: Date, jsEvent: MouseEvent }, calId:number) {
			if (this.isDragging) return;
			let toEditEvent = new CalendarEvent();
			toEditEvent.StartTime = event.date;
			toEditEvent.CalendarID = calId;
			toEditEvent.setEndTime(event.date);
			this.$emit("edit-event", toEditEvent);
		},
		inWichCalendarIsPos(clientX:number, clientY:number):number{
			let calRefs = (this.$refs.fullCalendar as Vue[]).map(c=>c.$el);
			for (let ref of calRefs) {
				let clientRect = ref.getBoundingClientRect();
				if (clientRect.left > clientX || clientX > clientRect.right) continue;
				if (clientRect.top > clientY || clientY > clientRect.bottom) continue;
				return parseInt(ref.getAttribute("data-calendar-id") || "0");
			}
			return 0;
		},
		async onDropStopHandler(event:{el:HTMLElement, event:FullCalendarApi.EventApi, jsEvent:MouseEvent}, fromCalendarId:number){
			this.$emit("eventDragStop", event);
			let calId = this.inWichCalendarIsPos(event.jsEvent.clientX, event.jsEvent.clientY);
			if (!calId) return;
			let targetCal = this.calendars.find(c=>c.ID == calId);
			if (!targetCal) return;
			if (targetCal.ID == fromCalendarId) return;
			let calEventId = event.event._def.extendedProps.id as number;
			let calEvent = await CalendarService.getEvent(calEventId);
			calEvent.CalendarID = targetCal.ID;
			await CalendarService.putEvents([calEvent]);
			this.state.refetchEvents();
			return;
		},
	},
	components: {
		AppFullCalendar,
	}
});
